// frontend/src/features/livestock/UploadProgressStrip.tsx
// Progress of the photo upload queue, mounted in ProtectedLayout so it shows on
// every page (docs/plans/2026-10-13-photo-inbox.md, 4.1). While a batch runs:
// "Checking 23 of 100", then "Uploading 7 of 23 to Display Tank", with a
// progress bar and why it is waiting, if it is. When it is done, a summary that
// stays until dismissed, with Sort now and the failures with Retry.
//
// On the inbox pages (/livestock/photos and below), which have a sticky bottom
// action bar, the strip is one line (label, bar, Details) that opens to the
// full view, sits above that bar (see --rdh-bottom-bar-h in the CSS), and a
// summary without failures dismisses itself: the photos are already in view.
//
// A batch from the Tank Shots page (assignTo 'tank', tank shots design 6.5)
// reads "Adding 7 of 23 to Tank Shots", then "12 added to Tank Shots · 1 left
// to sort" with Sort now; photos already on an organism are listed by name
// ("Already on Green Acro"), since a photo can be in one place.

import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCloudUpload,
  IconPlayerPause,
  IconX,
} from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { useIsMobile } from '@/hooks/useIsMobile';
// The inbox grid and the assign view, which have a bottom action bar.
import { INBOX_ROUTE } from '@/hooks/usePhotoInbox';
import {
  isRetryable,
  photoUploadQueue,
  usePhotoUploadQueue,
  type InterruptedToSort,
  type UploadBatchView,
  type UploadOutcome,
  type UploadPause,
} from './photoUploadQueue';
import { BRAND_TEXT } from './brandText';
import { useOpenInTank } from './useOpenInTank';
import classes from './UploadProgressStrip.module.css';

export interface UploadProgressStripProps {
  /** Whether the data-entry FAB is on screen at the bottom right; the strip keeps clear of it. */
  fabVisible?: boolean;
}

/** Space between the strip and the bottom of the page content (its 20 px offset plus a gap). */
const SPACER_EXTRA = 32;

const NBSP = ' ';

/**
 * Control sizes: 36 px touch targets on phones (Button sm, ActionIcon input-sm),
 * 44 px for the ones used most (Sort now, Details), compact on desktop.
 */
interface ControlSizes {
  button: 'sm' | 'xs';
  retry: 'sm' | 'compact-xs';
  icon: 'input-sm' | 'sm';
  /** Height of Sort now and Details. */
  main: number | undefined;
}

function controlSizes(isMobile: boolean): ControlSizes {
  return isMobile
    ? { button: 'sm', retry: 'sm', icon: 'input-sm', main: 44 }
    : { button: 'xs', retry: 'compact-xs', icon: 'sm', main: undefined };
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Seconds as "42 s", or whole minutes once the wait is two minutes or more. */
function formatWait(seconds: number): string {
  return seconds < 120 ? `${seconds} s` : `${Math.ceil(seconds / 60)} min`;
}

/** The current time, ticking every second while `ticking`. */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [ticking]);
  return now;
}

function isHeld(pause: UploadPause): pause is { kind: 'rate_limited' | 'busy'; resumeAt: number } {
  return pause?.kind === 'rate_limited' || pause?.kind === 'busy';
}

/** Why nothing is sent and for how long; `short` for the one-line strip. */
function holdText(pause: UploadPause, now: number, short: boolean): string | null {
  if (!isHeld(pause)) return null;
  const wait = formatWait(Math.max(0, Math.ceil((pause.resumeAt - now) / 1000)));
  if (pause.kind === 'busy') return `${short ? 'Busy' : 'Server busy'}, retrying in ${wait}`;
  return short ? `Paused, resuming in ${wait}` : `Paused: too many uploads, resuming in ${wait}`;
}

function isChecking(batch: UploadBatchView): boolean {
  return batch.stage === 'hashing' || batch.stage === 'checking';
}

/**
 * "Checking 23 of 100 for Display Tank" while hashing, then "Uploading 7 of 23
 * to Display Tank", or "Adding 7 of 23 to Tank Shots for Display Tank" for a
 * tank batch.
 */
function runningLabel(batch: UploadBatchView, withTank: boolean): string {
  if (isChecking(batch)) {
    const current = Math.min(batch.hashed + 1, batch.total);
    return `Checking ${current} of ${batch.total}${withTank ? ` for ${batch.tankName}` : ''}`;
  }
  const current = Math.min(batch.settled + 1, batch.total);
  if (batch.assignTo === 'tank') {
    return `Adding ${current} of ${batch.total} to Tank Shots${withTank ? ` for ${batch.tankName}` : ''}`;
  }
  return `Uploading ${current} of ${batch.total}${withTank ? ` to ${batch.tankName}` : ''}`;
}

/** Already on an organism: a tank batch couldn't add it (a photo is in one place). */
function onOrganism(outcome: UploadOutcome | null): boolean {
  return outcome?.kind === 'on_livestock' && outcome.target !== 'tank';
}

/** Percent for the bar: photos checked while checking, then the upload progress. */
function barValue(batch: UploadBatchView): number {
  const fraction = isChecking(batch) ? batch.hashed / Math.max(1, batch.total) : batch.progress;
  return Math.round(fraction * 100);
}

/** Photos that won't be uploaded unless the user acts: failed, or left when the batch stopped. */
function notUploadedOf(batch: UploadBatchView): number {
  return batch.files.filter(
    ({ outcome }) =>
      outcome?.kind === 'failed' || (outcome?.kind === 'skipped' && outcome.why === 'stopped')
  ).length;
}

/** A single photo's result in words, when the batch was one photo. */
function describeOne(outcome: UploadOutcome): string | null {
  if (outcome.kind === 'uploaded') return 'Uploaded';
  if (outcome.kind === 'assigned') return 'Added to Tank Shots';
  if (outcome.kind === 'left_in_inbox') return 'Left in Photos to sort';
  if (outcome.kind === 'in_inbox') return 'Already in the inbox';
  if (outcome.kind === 'on_livestock') {
    return outcome.target === 'tank'
      ? 'Already a tank shot'
      : `Already on ${outcome.livestockName}`;
  }
  return null;
}

/**
 * "21 uploaded · 2 already in inbox · 4 already sorted". Spaces within a part
 * are non-breaking, so a line only breaks after a separator.
 */
function summaryOf(batch: UploadBatchView): string {
  const only = batch.total === 1 ? batch.files[0]?.outcome : null;
  const single = only ? describeOne(only) : null;
  if (single) return single;

  const { counts } = batch;
  let pickedTwice = 0;
  let notUploaded = 0;
  const organisms: string[] = [];
  for (const { outcome } of batch.files) {
    if (outcome?.kind === 'skipped' && outcome.why === 'duplicate') pickedTwice++;
    if (outcome?.kind === 'skipped' && outcome.why === 'stopped') notUploaded++;
    if (outcome?.kind === 'on_livestock' && onOrganism(outcome))
      organisms.push(outcome.livestockName);
  }
  const tankShots = counts.onLivestock - organisms.length;
  const parts =
    batch.assignTo === 'tank'
      ? [
          counts.assigned && `${counts.assigned} added to Tank Shots`,
          tankShots && `${tankShots} already in Tank Shots`,
          organisms.length === 1 && `1 already on ${organisms[0]}`,
          // Several organisms: a count only, on purpose; the names are in the detail list.
          organisms.length > 1 && `${organisms.length} already on livestock`,
          counts.leftInInbox && `${counts.leftInInbox} left to sort`,
          pickedTwice && `${pickedTwice} picked twice`,
          counts.failed && `${counts.failed} failed`,
          notUploaded && `${notUploaded} not uploaded`,
        ]
      : [
          counts.uploaded && `${counts.uploaded} uploaded`,
          counts.inInbox && `${counts.inInbox} already in inbox`,
          counts.onLivestock && `${counts.onLivestock} already sorted`,
          pickedTwice && `${pickedTwice} picked twice`,
          counts.failed && `${counts.failed} failed`,
          notUploaded && `${notUploaded} not uploaded`,
        ];
  return parts
    .filter((part): part is string => Boolean(part))
    .map((part) => part.replace(/ /g, NBSP))
    .join(`${NBSP}· `);
}

/** Why a listed photo wasn't uploaded, or wasn't added to Tank Shots. */
function noteOf(outcome: UploadOutcome | null): string {
  if (outcome?.kind === 'failed') return outcome.message;
  if (outcome?.kind === 'left_in_inbox') return `Left in Photos to sort: ${outcome.reason}`;
  if (outcome?.kind === 'on_livestock') return `Already on ${outcome.livestockName}`;
  return '';
}

/**
 * After a reload: a tank batch's photos that were uploaded but not added wait
 * in that tank's Photos to sort. Without a tank (an older marker) there is no
 * Sort now.
 */
function WaitingToSort({
  entry,
  sizes,
}: {
  entry: InterruptedToSort;
  sizes: ControlSizes;
}): JSX.Element {
  const openInTank = useOpenInTank();
  const { tanks } = useTank();
  const { tankId, count } = entry;
  const tankName = tankId ? tanks.find((t) => t.id === tankId)?.name : undefined;
  const text = `${count === 1 ? '1 photo is' : `${count} photos are`} waiting in Photos to sort${
    tankName ? ` for ${tankName}` : ''
  }.`;

  const sortNow = (): void => {
    if (tankId === null) return;
    photoUploadQueue.dismissInterruptedToSort(tankId);
    openInTank(tankId, '/livestock/photos').catch(() => {
      // The tank couldn't be selected (gone, or offline): stay here.
    });
  };

  return (
    <Group gap="xs" wrap="nowrap" align="flex-start" justify="space-between">
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <IconAlertTriangle size={16} color="var(--rdh-accent)" style={{ flexShrink: 0 }} />
        <Stack gap={6} align="flex-start">
          <Text size="sm">{text}</Text>
          {tankId !== null && (
            <Button size={sizes.button} h={sizes.main} variant="light" onClick={sortNow}>
              Sort now
            </Button>
          )}
        </Stack>
      </Group>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={sizes.icon}
        aria-label="Dismiss photos waiting to sort"
        onClick={() => photoUploadQueue.dismissInterruptedToSort(tankId)}
        style={{ flexShrink: 0 }}
      >
        <IconX size={16} />
      </ActionIcon>
    </Group>
  );
}

function UploadIcon({ held }: { held: boolean }): JSX.Element {
  return held ? (
    <IconPlayerPause size={16} color="var(--rdh-accent)" style={{ flexShrink: 0 }} />
  ) : (
    <IconCloudUpload size={16} color="var(--mantine-color-teal-6)" style={{ flexShrink: 0 }} />
  );
}

function RunningBatch({
  batch,
  pause,
}: {
  batch: UploadBatchView;
  pause: UploadPause;
}): JSX.Element {
  const held = isHeld(pause);
  const now = useNow(held);
  const waiting = holdText(pause, now, false);
  const retrying = batch.files.filter((file) => file.stage === 'retrying').length;
  const note = waiting ?? (retrying > 0 ? `Retrying ${plural(retrying, 'photo')}` : null);

  return (
    <Stack gap={6}>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <UploadIcon held={held} />
        <Text size="sm" fw={500} className={classes.count} lh={1.3}>
          {runningLabel(batch, true)}
        </Text>
      </Group>
      <Progress
        value={barValue(batch)}
        color={held ? 'gray' : 'teal'}
        size="sm"
        aria-label="Upload progress"
      />
      {note && (
        <Text size="xs" c="coral.6" className={classes.count}>
          {note}
        </Text>
      )}
    </Stack>
  );
}

/** The strip on the inbox pages: one line, opened with Details. */
function CompactLine({
  active,
  pause,
  notUploaded,
  leftToSort,
  sizes,
  onDetails,
}: {
  active: UploadBatchView | undefined;
  pause: UploadPause;
  notUploaded: number;
  /** A tank batch's photos left in the inbox: they need sorting by hand. */
  leftToSort: number;
  sizes: ControlSizes;
  onDetails: () => void;
}): JSX.Element {
  const held = active !== undefined && isHeld(pause);
  const now = useNow(held);

  // While uploading, the bar (gray while held) stands in for the icon, leaving the label room.
  let icon: JSX.Element | null = null;
  let label: string;
  if (active) {
    label = holdText(pause, now, true) ?? runningLabel(active, false);
  } else if (notUploaded > 0) {
    icon = <IconAlertTriangle size={16} color="var(--rdh-accent)" style={{ flexShrink: 0 }} />;
    label = `${plural(notUploaded, 'photo')} didn't upload`;
  } else if (leftToSort > 0) {
    icon = <IconAlertTriangle size={16} color="var(--rdh-accent)" style={{ flexShrink: 0 }} />;
    label = `${leftToSort} left to sort`;
  } else {
    icon = (
      <IconCircleCheck size={16} color="var(--mantine-color-teal-6)" style={{ flexShrink: 0 }} />
    );
    label = 'Upload finished';
  }

  // The label keeps its width before the bar does; the bar takes what is left (at least 32 px).
  return (
    <Group gap={8} wrap="nowrap">
      {icon}
      <Text
        size="sm"
        fw={500}
        truncate
        title={label}
        className={classes.count}
        style={{ flex: '0 1 auto', minWidth: 0 }}
      >
        {label}
      </Text>
      {active ? (
        <Progress
          value={barValue(active)}
          color={held ? 'gray' : 'teal'}
          size="sm"
          style={{ flex: '1 1 32px', minWidth: 32 }}
          aria-label="Upload progress"
        />
      ) : (
        <Box style={{ flex: 1 }} />
      )}
      <Button
        variant="subtle"
        size={sizes.button}
        h={sizes.main}
        px="xs"
        aria-expanded={false}
        onClick={onDetails}
        style={{ ...BRAND_TEXT, flexShrink: 0 }}
      >
        Details
      </Button>
    </Group>
  );
}

function FinishedBatch({
  batch,
  sizes,
}: {
  batch: UploadBatchView;
  sizes: ControlSizes;
}): JSX.Element {
  const openInTank = useOpenInTank();
  const tank = batch.assignTo === 'tank';
  const failures = batch.files.filter((file) => file.outcome?.kind === 'failed');
  // Listed with their reason: failures, and a tank batch's photos it couldn't add.
  const notes = batch.files.filter(
    (file) =>
      file.outcome?.kind === 'failed' ||
      (tank && (file.outcome?.kind === 'left_in_inbox' || onOrganism(file.outcome)))
  );
  const retryable = notes.filter((file) => isRetryable(file.outcome));
  const stopped = batch.status === 'stopped';
  const happy = !stopped && failures.length === 0 && batch.counts.leftInInbox === 0;
  const canSort = tank
    ? batch.counts.leftInInbox > 0
    : batch.counts.uploaded + batch.counts.inInbox > 0;
  const onePlace = tank && notes.some((file) => onOrganism(file.outcome));

  const sortNow = (): void => {
    // Failures stay listed until dismissed, so their Retry isn't lost.
    if (failures.length === 0 && retryable.length === 0) photoUploadQueue.dismiss(batch.id);
    void openInTank(batch.tankId, '/livestock/photos');
  };

  return (
    <Stack gap={6}>
      <Group gap="xs" wrap="nowrap" align="flex-start" justify="space-between">
        <Group gap="xs" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
          {happy ? (
            <IconCircleCheck
              size={16}
              color="var(--mantine-color-teal-6)"
              style={{ flexShrink: 0 }}
            />
          ) : (
            <IconAlertTriangle size={16} color="var(--rdh-accent)" style={{ flexShrink: 0 }} />
          )}
          <Text size="sm" fw={500} lh={1.3}>
            {batch.tankName}: upload {stopped ? 'stopped' : 'finished'}
          </Text>
        </Group>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={sizes.icon}
          aria-label="Dismiss"
          onClick={() => photoUploadQueue.dismiss(batch.id)}
          style={{ flexShrink: 0 }}
        >
          <IconX size={16} />
        </ActionIcon>
      </Group>

      {batch.stopMessage && (
        <Text size="xs" c="coral.6">
          {batch.stopMessage}
        </Text>
      )}
      <Text size="sm" c="gray.7" className={classes.count}>
        {summaryOf(batch)}
      </Text>

      {onePlace && (
        <Text size="xs" c="dimmed">
          A photo can be in one place: on an organism or in Tank Shots.
        </Text>
      )}

      {notes.length > 0 && (
        <ScrollArea.Autosize mah={140} type="auto" offsetScrollbars>
          <Stack gap={4}>
            {notes.map((file) => (
              <Group key={file.id} gap="xs" wrap="nowrap" justify="space-between">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" truncate>
                    {file.name}
                  </Text>
                  <Text size="xs" c={file.outcome?.kind === 'failed' ? 'coral.6' : 'dimmed'}>
                    {noteOf(file.outcome)}
                  </Text>
                </Box>
                {isRetryable(file.outcome) && (
                  <Button
                    size={sizes.retry}
                    variant="light"
                    style={{ flexShrink: 0 }}
                    aria-label={`Retry ${file.name}`}
                    onClick={() => photoUploadQueue.retry(batch.id, file.id)}
                  >
                    Retry
                  </Button>
                )}
              </Group>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      )}

      {(canSort || retryable.length > 1) && (
        <Group gap="xs">
          {canSort && (
            <Button size={sizes.button} h={sizes.main} onClick={sortNow}>
              Sort now
            </Button>
          )}
          {retryable.length > 1 && (
            <Button
              size={sizes.button}
              variant="light"
              onClick={() => photoUploadQueue.retryFailed(batch.id)}
            >
              Retry all
            </Button>
          )}
        </Group>
      )}
    </Stack>
  );
}

export function UploadProgressStrip({
  fabVisible = true,
}: UploadProgressStripProps): JSX.Element | null {
  const { batches, pause, interrupted, interruptedToSort } = usePhotoUploadQueue();
  const { tanks, isInitialized } = useTank();
  const { ref, height } = useElementSize();
  const { pathname } = useLocation();
  const sizes = controlSizes(useIsMobile());
  const besideBar = INBOX_ROUTE.test(pathname);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    photoUploadQueue.restoreInterrupted();
  }, []);

  useEffect(() => {
    if (!besideBar) setExpanded(false);
  }, [besideBar]);

  // On the inbox pages the uploaded photos are already in view: a summary with
  // nothing to retry would only cover the action bar.
  useEffect(() => {
    if (!besideBar) return;
    for (const batch of batches) {
      // A tank batch's summary stays: Sort now leads here, and its Retry must not be lost.
      if (
        batch.status === 'finished' &&
        batch.counts.failed === 0 &&
        batch.assignTo !== 'tank' &&
        !batch.files.some((file) => isRetryable(file.outcome))
      ) {
        photoUploadQueue.dismiss(batch.id);
      }
    }
  }, [besideBar, batches]);

  const running = batches.find((batch) => batch.status === 'running');
  const waiting = batches.filter((batch) => batch.status === 'waiting');
  const done = batches.filter((batch) => batch.status === 'finished' || batch.status === 'stopped');

  // Once the tanks list has loaded, a tank deleted meanwhile has nothing to sort.
  const waitingToSort = interruptedToSort.filter(
    (entry) => entry.tankId === null || !isInitialized || tanks.some((t) => t.id === entry.tankId)
  );

  if (batches.length === 0 && interrupted === 0 && waitingToSort.length === 0) return null;

  const notUploaded = done.reduce((sum, batch) => sum + notUploadedOf(batch), interrupted);
  const leftToSort = done.reduce(
    (sum, batch) => sum + batch.counts.leftInInbox,
    waitingToSort.reduce((sum, entry) => sum + entry.count, 0)
  );

  return (
    <>
      {/* Room at the end of the page so the last content can scroll clear of the strip. */}
      <Box aria-hidden h={height ? height + SPACER_EXTRA : 0} />
      <Paper
        ref={ref}
        component="section"
        role="region"
        aria-label="Photo uploads"
        withBorder
        radius="lg"
        p="sm"
        className={classes.root}
        mod={{ 'with-fab': fabVisible }}
      >
        {besideBar && !expanded ? (
          <CompactLine
            active={running ?? waiting[0]}
            pause={pause}
            notUploaded={notUploaded}
            leftToSort={leftToSort}
            sizes={sizes}
            onDetails={() => setExpanded(true)}
          />
        ) : (
          <Stack gap="sm">
            {interrupted > 0 && (
              <Group gap="xs" wrap="nowrap" align="flex-start" justify="space-between">
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <IconAlertTriangle
                    size={16}
                    color="var(--rdh-accent)"
                    style={{ flexShrink: 0 }}
                  />
                  <Text size="sm">
                    {plural(interrupted, 'photo')} didn&apos;t upload. Pick the same photos again;
                    ones already uploaded are skipped.
                  </Text>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={sizes.icon}
                  aria-label="Dismiss"
                  onClick={() => photoUploadQueue.dismissInterrupted()}
                  style={{ flexShrink: 0 }}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            )}

            {waitingToSort.map((entry) => (
              <WaitingToSort key={entry.tankId ?? ''} entry={entry} sizes={sizes} />
            ))}

            {running && <RunningBatch batch={running} pause={pause} />}
            {waiting.map((batch) => (
              <Text key={batch.id} size="xs" c="gray.7" className={classes.count}>
                Then {batch.total - batch.settled} more for {batch.tankName}
              </Text>
            ))}
            {done.map((batch) => (
              <FinishedBatch key={batch.id} batch={batch} sizes={sizes} />
            ))}

            {besideBar && (
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  size={sizes.button}
                  h={sizes.main}
                  aria-expanded
                  onClick={() => setExpanded(false)}
                  style={BRAND_TEXT}
                >
                  Hide details
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Paper>
    </>
  );
}
